// ---------------------------------------------------------------------------
// Test Mode — task templates (CLE-DASH).
//
// A couple of realistic templates so the Settings tab and the Create Project
// modal have data to render. Department refs point at the test departments.
// ---------------------------------------------------------------------------

import type { TaskTemplate, TaskTemplateItem } from '../lib/taskTemplatesApi';
import type { TaskPriority } from '../lib/tasksApi';
import { DEPARTMENT_SEEDS } from './departments';
import { isoDateTime } from './company';
import { TEST_LABELS } from './tasks';

function deptRef(id: string | null): { id: string; name: string; color: string } | null {
  if (!id) return null;
  const d = DEPARTMENT_SEEDS.find((s) => s.id === id);
  return d ? { id: d.id, name: d.name, color: d.color } : null;
}

function item(
  templateId: string,
  order: number,
  title: string,
  opts: {
    description?: string;
    priority?: TaskPriority;
    estimate?: number;
    departmentId?: string;
    labelIds?: string[];
  } = {},
): TaskTemplateItem {
  return {
    id: `${templateId}-item-${order}`,
    title,
    description: opts.description ?? null,
    priority: opts.priority ?? 'NONE',
    estimate: opts.estimate ?? null,
    departmentId: opts.departmentId ?? null,
    department: deptRef(opts.departmentId ?? null),
    labels: TEST_LABELS.filter((l) => (opts.labelIds ?? []).includes(l.id)).map((l) => ({
      id: l.id,
      name: l.name,
      color: l.color,
    })),
    order,
  };
}

export function buildTaskTemplates(): TaskTemplate[] {
  const kickoffItems = [
    item('tpl-kickoff', 0, 'Setup repository și CI/CD', {
      labelIds: ['lbl-feature', 'lbl-tech-debt'],
      priority: 'HIGH',
      estimate: 4,
      departmentId: 'dept-infrastructure',
    }),
    item('tpl-kickoff', 1, 'Definire arhitectură tehnică', {
      description: 'Diagramă de arhitectură + decizii de stack.',
      priority: 'HIGH',
      estimate: 8,
      departmentId: 'dept-engineering',
    }),
    item('tpl-kickoff', 2, 'Wireframes ecrane principale', {
      priority: 'MEDIUM',
      estimate: 12,
      departmentId: 'dept-design',
    }),
    item('tpl-kickoff', 3, 'Plan de testare inițial', {
      priority: 'MEDIUM',
      estimate: 4,
      departmentId: 'dept-quality',
    }),
    item('tpl-kickoff', 4, 'Kick-off meeting cu clientul', {
      priority: 'URGENT',
      estimate: 2,
      departmentId: 'dept-delivery',
    }),
  ];

  const launchItems = [
    item('tpl-launch', 0, 'Audit de securitate pre-lansare', {
      priority: 'URGENT',
      estimate: 8,
      departmentId: 'dept-engineering',
    }),
    item('tpl-launch', 1, 'Testare de regresie completă', {
      priority: 'HIGH',
      estimate: 16,
      departmentId: 'dept-quality',
    }),
    item('tpl-launch', 2, 'Configurare monitorizare și alerte', {
      priority: 'HIGH',
      estimate: 4,
      departmentId: 'dept-infrastructure',
    }),
    item('tpl-launch', 3, 'Materiale de anunț lansare', {
      priority: 'MEDIUM',
      estimate: 6,
      departmentId: 'dept-marketing',
    }),
  ];

  return [
    {
      id: 'tpl-kickoff',
      name: 'Kick-off proiect',
      description: 'Sarcinile standard de pornire pentru un proiect nou.',
      departmentId: null,
      department: null,
      itemCount: kickoffItems.length,
      items: kickoffItems,
      createdAt: isoDateTime(-200, 10),
      updatedAt: isoDateTime(-14, 11),
    },
    {
      id: 'tpl-launch',
      name: 'Checklist lansare',
      description: 'Verificările finale înainte de lansarea în producție.',
      departmentId: 'dept-quality',
      department: deptRef('dept-quality'),
      itemCount: launchItems.length,
      items: launchItems,
      createdAt: isoDateTime(-120, 10),
      updatedAt: isoDateTime(-7, 15),
    },
  ];
}
